import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import FamilyTree from '../components/tree/FamilyTree';
import AddMemberForm from '../components/member/AddMemberForm';
import MemberPanel from '../components/member/MemberPanel';
import UpgradeModal from '../components/Premium/UpgradeModal';
import FamilyGallery from '../components/gallery/FamilyGallery';
import FamilyTraditions from '../components/traditions/FamilyTraditions';
import FamilyEvents from '../components/traditions/FamilyEvents';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [spouseTarget, setSpouseTarget] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [refreshKey, setRefreshKey] = useState(0);
  const treeRef = useRef(null);
  const [activeBranch, setActiveBranch] = useState('paternal');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [activeTab, setActiveTab] = useState('tree');
  const [upcomingEventCount, setUpcomingEventCount] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [addContext, setAddContext] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importConflict, setImportConflict] = useState(null);
  const fileInputRef = useRef(null);

  const handleMemberAdded = () => {
    setShowAddForm(false);
    setSpouseTarget(null);
    setAddContext(null);
    if (treeRef.current) treeRef.current.refresh();
  };

  const handleAddSpouse = (member) => {
    setSpouseTarget(member);
    setAddContext(null);
    setShowAddForm(true);
  };

  React.useEffect(() => {
    if (!user?.email) return;
    fetch(`https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}/family-name`)
      .then(r => r.json())
      .then(data => {
        if (data.familyName) setFamilyName(data.familyName);
        if (data.isPremium) setIsPremium(true);
      })
      .catch(() => {});
  }, [user?.email]);

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) { setEditingName(false); return; }
    try {
      await fetch(`https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(user.email)}/family-name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyName: trimmed }),
      });
      setFamilyName(trimmed);
      setEditingName(false);
    } catch (err) {
      setEditingName(false);
    }
  };

  // ── Handle .fmly file import ──────────────────────────────────────────────
  // Note: Web app uses OTP-based custom auth, not Firebase Auth tokens.
  // Authentication is handled by the backend via email ownership check.
  const handleFmlyFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setImportConflict(null);
    try {
      const text = await file.text();
      const tree = JSON.parse(text);
      const res = await fetch('https://familybandhan-backend-933326390640.asia-south1.run.app/api/import/fmly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyEmail: user.email, tree, replace: false }),
      });
      const data = await res.json();
      if (data.conflict) {
        setImportConflict({ existingCount: data.existingCount, tree });
      } else if (data.success) {
        setImportResult({ imported: data.imported });
      } else {
        alert(data.message || 'Import failed. Please try again.');
      }
    } catch (err) {
      alert('Could not read the file. Please make sure it is a valid .fmly file.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleConfirmReplace = async () => {
    if (!importConflict?.tree) return;
    setImporting(true);
    try {
      const res = await fetch('https://familybandhan-backend-933326390640.asia-south1.run.app/api/import/fmly', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyEmail: user.email, tree: importConflict.tree, replace: true }),
      });
      const data = await res.json();
      if (data.success) {
        setImportResult({ imported: data.imported });
        setImportConflict(null);
      } else {
        alert(data.message || 'Import failed. Please try again.');
      }
    } catch (err) {
      alert('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleAddInColumn = (generation) => {
    setSpouseTarget(null);
    setAddContext({ generation, branch: activeBranch });
    setShowAddForm(true);
  };

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 599px) {
          .fb-user-email { display: none; }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logoFamily}>Family</span>
          <span style={styles.logoBandhan}>Bandhan</span>
        </div>

        {/* Centre — Family Name */}
        <div style={styles.headerCenter}>
          {editingName ? (
            <div style={styles.nameEditRow}>
              <input
                autoFocus
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                style={styles.nameInput}
                maxLength={40}
              />
              <button onClick={handleSaveName} style={styles.nameSaveBtn}>Save</button>
              <button onClick={() => setEditingName(false)} style={styles.nameCancelBtn}>✕</button>
            </div>
          ) : (
            <div
              onClick={() => { setNameInput(familyName); setEditingName(true); }}
              style={styles.nameDisplay}
              title="Click to rename your family tree"
            >
              <span style={familyName ? styles.nameText : styles.namePlaceholder}>
                {familyName || 'Your Family'}
              </span>
              <span style={styles.nameEditIcon}>✎</span>
            </div>
          )}
          <div style={styles.headerEmail}>{user?.email}</div>
        </div>

        {/* Right — buttons */}
        <div style={styles.headerRight}>
          <button onClick={() => setShowImport(true)} style={styles.importBtn}>
            📂 Import .fmly
          </button>
          {!isPremium && (
            <button onClick={() => setShowUpgrade(true)} style={styles.upgradeBtn}>
              👑 Upgrade
            </button>
          )}
          {isPremium && (
            <span style={styles.premiumBadge}>👑 Premium</span>
          )}
          <button onClick={logout} style={styles.logoutBtn}>Sign out</button>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={styles.tabSwitcher}>
        <button onClick={() => setActiveTab('tree')} style={{ ...styles.tabBtn, ...(activeTab === 'tree' ? styles.tabBtnActive : {}) }}>
          🌳 Family Tree
        </button>
        <button onClick={() => { if (!isPremium) { setShowUpgrade(true); return; } setActiveTab('gallery'); }} style={{ ...styles.tabBtn, ...(activeTab === 'gallery' ? styles.tabBtnActive : {}) }}>
          📷 Family Gallery {!isPremium && <span style={styles.lockIcon}>🔒</span>}
        </button>
        <button onClick={() => { if (!isPremium) { setShowUpgrade(true); return; } setActiveTab('traditions'); }} style={{ ...styles.tabBtn, ...(activeTab === 'traditions' ? styles.tabBtnActive : {}) }}>
          📜 Traditions {!isPremium && <span style={styles.lockIcon}>🔒</span>}
        </button>
        <button onClick={() => { if (!isPremium) { setShowUpgrade(true); return; } setActiveTab('events'); }} style={{ ...styles.tabBtn, ...(activeTab === 'events' ? styles.tabBtnActive : {}) }}>
          📅 Events {!isPremium && <span style={styles.lockIcon}>🔒</span>}
          {isPremium && upcomingEventCount > 0 && <span style={styles.notifDot}>{upcomingEventCount}</span>}
        </button>
      </div>

      {/* Import from Desktop Modal */}
      {showImport && (
        <div style={styles.importOverlay}>
          <div style={styles.importCard}>
            <button
              onClick={() => { setShowImport(false); setImportResult(null); setImportConflict(null); }}
              style={styles.importClose}
            >×</button>

            <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
            <h3 style={styles.importTitle}>Import from Desktop</h3>
            <p style={styles.importDesc}>
              Select your <strong>.fmly</strong> file to import your offline family tree into your online account.
              All text data transfers instantly. Photos will need to be re-uploaded manually.
            </p>

            {!importResult && !importConflict && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".fmly"
                  style={{ display: 'none' }}
                  onChange={handleFmlyFileSelect}
                />
                <button
                  onClick={() => fileInputRef.current.click()}
                  disabled={importing}
                  style={styles.importSelectBtn}
                >
                  {importing ? 'Importing...' : 'Select .fmly file'}
                </button>
              </>
            )}

            {importConflict && !importResult && (
              <div style={styles.importConflictBox}>
                <p style={styles.importConflictText}>
                  ⚠️ You already have an online tree with <strong>{importConflict.existingCount} members</strong>.
                  Importing will permanently replace it with your desktop tree.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
                  <button
                    onClick={() => { setImportConflict(null); setImportResult(null); }}
                    style={styles.importCancelBtn}
                  >
                    Cancel — Keep my online tree
                  </button>
                  <button
                    onClick={handleConfirmReplace}
                    disabled={importing}
                    style={styles.importReplaceBtn}
                  >
                    {importing ? 'Replacing...' : 'Replace with desktop tree'}
                  </button>
                </div>
              </div>
            )}

            {importResult && (
              <div style={styles.importSuccessBox}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <p style={{ fontSize: 15, fontWeight: 600, color: '#1a0533', marginBottom: 8 }}>
                  {importResult.imported} members imported successfully!
                </p>
                <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                  Your family tree is now online. Profile photos could not be transferred — please re-upload them from each member's profile.
                </p>
                <button
                  onClick={() => {
                    setShowImport(false);
                    setImportResult(null);
                    setImportConflict(null);
                    if (treeRef.current) treeRef.current.refresh();
                  }}
                  style={styles.importSelectBtn}
                >
                  View my tree →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tree */}
      {activeTab === 'tree' && (
        <div style={styles.treeContainer}>
          <FamilyTree
            ref={treeRef}
            onMemberClick={(member) => setSelectedMember(member)}
            onAddSpouse={handleAddSpouse}
            onTabChange={(branch) => setActiveBranch(branch)}
            onAddInColumn={handleAddInColumn}
          />
        </div>
      )}

      {/* Gallery */}
      {activeTab === 'gallery' && (
        <div style={styles.treeContainer}>
          <FamilyGallery
            familyEmail={user.email}
            onViewProfile={(member) => {
              setActiveTab('tree');
              setSelectedMember(member);
            }}
          />
        </div>
      )}

      {/* Traditions */}
      {activeTab === 'traditions' && (
        <div style={styles.treeContainer}>
          <FamilyTraditions familyEmail={user.email} />
        </div>
      )}

      {/* Events */}
      {activeTab === 'events' && (
        <div style={styles.treeContainer}>
          <FamilyEvents
            familyEmail={user.email}
            onUpcomingCount={(count) => setUpcomingEventCount(count)}
          />
        </div>
      )}

      {/* Add Member Modal */}
      {showAddForm && (
        <div style={styles.overlay}>
          <AddMemberForm
            key={spouseTarget?.memberId || (addContext ? `gen-${addContext.generation}-${addContext.branch}` : 'blank')}
            familyEmail={user.email}
            spouseTarget={spouseTarget}
            defaultBranch={activeBranch}
            lockedGeneration={addContext?.generation}
            lockedBranch={addContext?.branch}
            onMemberAdded={handleMemberAdded}
            onClose={() => { setShowAddForm(false); setSpouseTarget(null); setAddContext(null); }}
          />
        </div>
      )}

      {/* Member detail panel */}
      {selectedMember && (
        <MemberPanel
          member={selectedMember}
          familyEmail={user.email}
          onClose={() => setSelectedMember(null)}
          onMemberUpdated={() => {
            setSelectedMember(null);
            treeRef.current?.refresh();
          }}
        />
      )}

      {/* Upgrade Modal */}
      {showUpgrade && (
        <UpgradeModal
          familyEmail={user.email}
          onClose={() => setShowUpgrade(false)}
          onSuccess={() => {
            setIsPremium(true);
            setShowUpgrade(false);
          }}
        />
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background:'#DBEAFE', border:'1.5px solid #93C5FD'}} />
            <span>Male</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background:'#FCE7F3', border:'1.5px solid #F9A8D4'}} />
            <span>Female</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background:'#FEF9C3', border:'2.5px solid #D4A017'}} />
            <span>Creator</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background:'#E5E7EB', border:'1.5px solid #9CA3AF'}} />
            <span>Deceased</span>
          </div>
          <div style={styles.legendItem}>
            <span style={{fontSize:'10px'}}>🔗</span>
            <span>Linked tree</span>
          </div>
          <div style={styles.legendSep} />
          <span style={styles.hint}>
            <b>Single click</b> — view &amp; edit details &nbsp;·&nbsp;
            <b>Triple click</b> — toggle deceased 🪔 &nbsp;·&nbsp;
            <b>Right click</b> — add spouse / delete &nbsp;·&nbsp;
            <b>+</b> — add a member to that generation
          </span>
        </div>
        <div style={styles.tip}>
          💡 Tip: Click the <b>+</b> at the bottom of any generation column to add a member there — your tree fills in as you go.
        </div>
        <div style={styles.disclaimer}>
          FamilyBandhan is a tool to map and celebrate family connections across generations.
          Please do not upload sensitive, confidential, or private information.
          Keep stories and photos joyful and family-friendly.
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh', maxWidth: '100vw', overflowX: 'hidden',
    display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', Arial, sans-serif", background: '#f8f7ff',
  },
  header: {
    background: '#0e0018', padding: '8px 16px', minHeight: '52px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: '8px', flexShrink: 0,
  },
  headerLeft: { fontFamily: "'Cormorant Garamond', serif", fontSize: '22px' },
  logoFamily: { color: '#ffffff' },
  logoBandhan: { color: '#D4A017' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerCenter: {
    position: 'absolute', left: '50%', transform: 'translateX(-50%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
  },
  nameDisplay: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  nameText: {
    color: '#ffffff', fontSize: '22px', fontWeight: '600',
    fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.5px',
  },
  namePlaceholder: {
    color: 'rgba(255,255,255,0.35)', fontSize: '22px',
    fontStyle: 'italic', fontFamily: "'Cormorant Garamond', serif",
  },
  nameEditIcon: { color: '#D4A017', fontSize: '12px', opacity: 0.7 },
  headerEmail: { color: 'rgba(255,255,255,0.3)', fontSize: '10px', textAlign: 'center' },
  nameEditRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  nameInput: {
    background: '#2b0a38', border: '1px solid #D4A017', color: '#ffffff',
    padding: '6px 14px', borderRadius: '6px', fontSize: '18px',
    width: '280px', outline: 'none', fontFamily: "'Cormorant Garamond', serif",
  },
  nameSaveBtn: {
    background: '#D4A017', border: 'none', color: '#1a0533',
    padding: '4px 10px', borderRadius: '6px', fontSize: '11px',
    fontWeight: '600', cursor: 'pointer',
  },
  nameCancelBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
    color: 'rgba(255,255,255,0.6)', padding: '4px 8px',
    borderRadius: '6px', fontSize: '11px', cursor: 'pointer',
  },
  importBtn: {
    background: 'transparent', border: '1px solid rgba(212,160,23,0.5)',
    color: '#D4A017', padding: '5px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  upgradeBtn: {
    background: 'linear-gradient(135deg, #7C3AED, #D4A017)', color: '#ffffff',
    border: 'none', padding: '5px 12px', borderRadius: '6px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  premiumBadge: { color: '#D4A017', fontSize: '12px', fontWeight: '600' },
  logoutBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.3)',
    color: '#ffffff', padding: '5px 12px', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer',
  },
  tabSwitcher: {
    background: '#13132a', borderBottom: '1px solid rgba(212,160,23,0.2)',
    display: 'flex', gap: 0, padding: '0 16px', flexShrink: 0,
  },
  tabBtn: {
    padding: '9px 18px', fontSize: 13, color: 'rgba(255,255,255,0.45)',
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    cursor: 'pointer', fontFamily: "'DM Sans', Arial, sans-serif", fontWeight: 400,
  },
  tabBtnActive: { color: '#D4A017', borderBottom: '2px solid #D4A017', fontWeight: 500 },
  lockIcon: { fontSize: 10, marginLeft: 4, opacity: 0.7 },
  notifDot: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 16, height: 16, borderRadius: '50%', background: '#E24B4A',
    color: '#fff', fontSize: 9, fontWeight: 700, marginLeft: 5, verticalAlign: 'middle',
  },
  treeContainer: {
    flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch',
    display: 'flex', flexDirection: 'column',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  footer: {
    flexShrink: 0, background: '#ffffff', borderTop: '1px solid #E5E7EB',
    padding: '8px 24px 6px', display: 'flex', flexDirection: 'column', gap: '5px',
  },
  legend: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#6B7280' },
  legendDot: { width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 },
  legendSep: { width: '1px', height: '14px', background: '#E5E7EB', margin: '0 4px' },
  hint: { fontSize: '11px', color: '#9CA3AF' },
  tip: { fontSize: '11px', color: '#9CA3AF', fontStyle: 'italic' },
  disclaimer: {
    fontSize: '10px', color: '#9CA3AF', textAlign: 'center',
    letterSpacing: '0.01em', lineHeight: 1.5,
  },
  addBtn: {
    background: '#D4A017', color: '#0e0018', border: 'none',
    padding: '6px 14px', borderRadius: '6px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  userEmail: { color: 'rgba(255,255,255,0.5)', fontSize: '12px' },
  importOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  importCard: {
    background: '#fff', borderRadius: 16, padding: '40px 36px',
    maxWidth: 480, width: '100%', textAlign: 'center',
    position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  importClose: {
    position: 'absolute', top: 16, right: 16, background: 'none',
    border: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF',
  },
  importTitle: {
    fontSize: 22, color: '#1a0533',
    fontFamily: "'Cormorant Garamond', serif", marginBottom: 8,
  },
  importDesc: { fontSize: 13, color: '#6B7280', lineHeight: 1.7, marginBottom: 24 },
  importSelectBtn: {
    background: '#1a0533', color: '#D4A017', border: 'none',
    borderRadius: 8, padding: '12px 28px', fontSize: 14,
    fontWeight: 600, cursor: 'pointer', marginTop: 8,
  },
  importConflictBox: {
    background: '#FEF9C3', border: '1px solid #D4A017',
    borderRadius: 10, padding: '16px 20px', marginTop: 16,
  },
  importConflictText: { fontSize: 13, color: '#1a0533', lineHeight: 1.7 },
  importCancelBtn: {
    background: '#fff', border: '1px solid #E5E7EB', color: '#6B7280',
    borderRadius: 8, padding: '10px 16px', fontSize: 13, cursor: 'pointer',
  },
  importReplaceBtn: {
    background: '#DC2626', color: '#fff', border: 'none',
    borderRadius: 8, padding: '10px 16px', fontSize: 13,
    fontWeight: 600, cursor: 'pointer',
  },
  importSuccessBox: {
    background: '#F0FDF4', border: '1px solid #86EFAC',
    borderRadius: 10, padding: '20px', marginTop: 16,
  },
};

export default Dashboard;
